/** Semantic badge and filter-chip colors for Community categories. */

const FILTER_CHIP_BASE = {
  fontSize: '0.8125rem',
  height: 30,
  borderRadius: 999,
};

const BADGE_CHIP_BASE = {
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 500,
  borderRadius: 999,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    px: 1,
  },
};

export const COMMUNITY_CATEGORY_PALETTES = {
  'sales & gtm': { bgcolor: '#FFF1F2', color: '#E11D48', borderColor: 'rgba(225, 29, 72, 0.2)' },
  'marketing & content': { bgcolor: '#FFFBEB', color: '#D97706', borderColor: 'rgba(217, 119, 6, 0.22)' },
  'customer success & support': { bgcolor: '#ECFEFF', color: '#0891B2', borderColor: 'rgba(8, 145, 178, 0.22)' },
  operations: { bgcolor: '#F8FAFC', color: '#475569', borderColor: '#E2E8F0' },
  'product & design': { bgcolor: '#EEF2FF', color: '#4F46E5', borderColor: 'rgba(79, 70, 229, 0.22)' },
  'engineering & it': { bgcolor: '#EFF6FF', color: '#1D4ED8', borderColor: 'rgba(29, 78, 216, 0.22)' },
  'data & analytics': { bgcolor: '#F0FDFA', color: '#0D9488', borderColor: 'rgba(13, 148, 136, 0.22)' },
  'finance & legal': { bgcolor: '#ECFDF5', color: '#059669', borderColor: 'rgba(5, 150, 105, 0.22)' },
  'people & hr': { bgcolor: '#FDF2F8', color: '#DB2777', borderColor: 'rgba(219, 39, 119, 0.22)' },
  'strategy & planning': { bgcolor: '#F5F3FF', color: '#7C3AED', borderColor: 'rgba(124, 58, 237, 0.22)' },
  'research & learning': { bgcolor: '#FAF5FF', color: '#9333EA', borderColor: 'rgba(147, 51, 234, 0.22)' },
  'compliance & risk': { bgcolor: '#F1F5F9', color: '#334155', borderColor: 'rgba(51, 65, 85, 0.18)' },
  engineering: { bgcolor: '#EFF6FF', color: '#1D4ED8', borderColor: 'rgba(29, 78, 216, 0.22)' },
  marketing: { bgcolor: '#FFFBEB', color: '#D97706', borderColor: 'rgba(217, 119, 6, 0.22)' },
  sales: { bgcolor: '#FFF1F2', color: '#E11D48', borderColor: 'rgba(225, 29, 72, 0.2)' },
  'product management': { bgcolor: '#F5F3FF', color: '#7C3AED', borderColor: 'rgba(124, 58, 237, 0.22)' },
  'customer support': { bgcolor: '#ECFEFF', color: '#0891B2', borderColor: 'rgba(8, 145, 178, 0.22)' },
  'business development': { bgcolor: '#FFFBEB', color: '#B45309', borderColor: 'rgba(180, 83, 9, 0.22)' },
  'accounting & finance': { bgcolor: '#ECFDF5', color: '#059669', borderColor: 'rgba(5, 150, 105, 0.22)' },
  legal: { bgcolor: '#FEF2F2', color: '#DC2626', borderColor: 'rgba(220, 38, 38, 0.2)' },
  'human resources': { bgcolor: '#FDF2F8', color: '#DB2777', borderColor: 'rgba(219, 39, 119, 0.22)' },
  'design & ux': { bgcolor: '#EEF2FF', color: '#4F46E5', borderColor: 'rgba(79, 70, 229, 0.22)' },
  'project management': { bgcolor: '#F1F5F9', color: '#334155', borderColor: 'rgba(51, 65, 85, 0.18)' },
  'research & development': { bgcolor: '#FAF5FF', color: '#9333EA', borderColor: 'rgba(147, 51, 234, 0.22)' },
  'it & infrastructure': { bgcolor: '#F0F9FF', color: '#0284C7', borderColor: 'rgba(2, 132, 199, 0.22)' },
  other: { bgcolor: '#F8FAFC', color: '#64748B', borderColor: '#E2E8F0' },
  all: { bgcolor: '#F8FAFC', color: '#64748B', borderColor: '#E2E8F0' },
};

export function normalizeCommunityCategory(category) {
  return String(category || '').trim().toLowerCase();
}

export function getCommunityCategoryPalette(category) {
  const key = normalizeCommunityCategory(category);
  return COMMUNITY_CATEGORY_PALETTES[key] || COMMUNITY_CATEGORY_PALETTES.other;
}

/** Filter row chips — selected uses active blue; unselected uses semantic tint. */
export function getCommunityCategoryFilterChipSx(category, selected) {
  const fontWeight = selected ? 600 : 500;

  if (selected) {
    return {
      ...FILTER_CHIP_BASE,
      fontWeight,
      borderColor: 'rgba(29, 78, 216, 0.35)',
      bgcolor: 'rgba(29, 78, 216, 0.08)',
      color: '#1D4ED8',
      '&:hover': {
        bgcolor: 'rgba(29, 78, 216, 0.12)',
        borderColor: 'rgba(29, 78, 216, 0.45)',
      },
    };
  }

  const key = normalizeCommunityCategory(category);
  if (key === 'all' || !key) {
    return {
      ...FILTER_CHIP_BASE,
      fontWeight,
      borderColor: '#E2E8F0',
      bgcolor: 'transparent',
      color: '#64748B',
      '&:hover': {
        bgcolor: 'rgba(15, 23, 42, 0.04)',
        borderColor: '#CBD5E1',
      },
    };
  }

  const palette = getCommunityCategoryPalette(category);
  return {
    ...FILTER_CHIP_BASE,
    fontWeight,
    bgcolor: palette.bgcolor,
    color: palette.color,
    borderColor: palette.borderColor,
    '&:hover': {
      bgcolor: palette.bgcolor,
      borderColor: palette.borderColor,
      boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)',
    },
  };
}

/** Category badge on community discovery cards — muted tint so color reads as metadata, not alert. */
export function getCommunityCategoryBadgeSx(category) {
  const palette = getCommunityCategoryPalette(category);
  return {
    ...BADGE_CHIP_BASE,
    maxWidth: { xs: '100%', sm: 160 },
    bgcolor: palette.bgcolor,
    color: `color-mix(in srgb, ${palette.color} 62%, #64748B)`,
    borderColor: `color-mix(in srgb, ${palette.color} 14%, transparent)`,
  };
}
